import { CLIP_EDGE, CLIP_MIN_SP, FRAME_SPECS, MAX_SPLICE, OBS_ZONE, ASSEMBLY_STEP_TYPES, BOM_TYPES, BOM_NAMES, HARDWARE_LABELS, } from './frameConstants';
import { HangingType } from './segFrameBuilderTypes';
// ============================================================
// PHASE 1: SEGMENTATION — Splice point layout for rail members
// ============================================================
/**
 * Divides a linear rail member into evenly-spaced segments, each no longer than
 * the maximum splice length. Returns the segment count, uniform segment lengths,
 * and cumulative start positions measured from the rail origin.
 */
export function buildSegments(totalRailLengthInches, maxSegmentLengthInches = MAX_SPLICE) {
    if (!Number.isFinite(totalRailLengthInches) || totalRailLengthInches <= 0) {
        return { segmentCount: 1, segmentLengths: [0], segmentStartPositions: [0] };
    }
    const segmentCount = Math.max(1, Math.ceil(totalRailLengthInches / maxSegmentLengthInches));
    const uniformSegmentLengthInches = totalRailLengthInches / segmentCount;
    const segmentLengths = Array(segmentCount).fill(uniformSegmentLengthInches);
    const segmentStartPositions = [0];
    for (let segmentIndex = 1; segmentIndex < segmentCount; segmentIndex++) {
        segmentStartPositions[segmentIndex] =
            segmentStartPositions[segmentIndex - 1] + segmentLengths[segmentIndex - 1];
    }
    return { segmentCount, segmentLengths, segmentStartPositions };
}
// ============================================================
// PHASE 2: CLIP PLACEMENT — Wall-mounting clip position resolution
// ============================================================
/**
 * Returns true if a candidate clip position falls within the exclusion zone
 * of any obstruction (e.g., a splice connector or an internal support member).
 */
function isClipPositionBlockedByObstruction(candidatePositionInches, obstructionCenterPositions) {
    return obstructionCenterPositions?.some(obstructionCenter => candidatePositionInches >= obstructionCenter - OBS_ZONE &&
        candidatePositionInches <= obstructionCenter + OBS_ZONE) ?? false;
}
/**
 * Searches outward from a blocked target position to find the nearest location
 * that is unobstructed and within the allowable segment bounds.
 * Returns the original target if no safe position is found within the search radius.
 */
function findNearestUnobstructedClipPosition(targetPositionInches, segmentMinBoundInches, segmentMaxBoundInches, obstructionCenterPositions) {
    if (!isClipPositionBlockedByObstruction(targetPositionInches, obstructionCenterPositions)) {
        return targetPositionInches;
    }
    let searchOffsetInches = 0.5;
    while (searchOffsetInches < 20) {
        const leftCandidateInches = targetPositionInches - searchOffsetInches;
        const rightCandidateInches = targetPositionInches + searchOffsetInches;
        if (leftCandidateInches >= segmentMinBoundInches &&
            !isClipPositionBlockedByObstruction(leftCandidateInches, obstructionCenterPositions)) {
            return leftCandidateInches;
        }
        if (rightCandidateInches <= segmentMaxBoundInches &&
            !isClipPositionBlockedByObstruction(rightCandidateInches, obstructionCenterPositions)) {
            return rightCandidateInches;
        }
        searchOffsetInches += 0.5;
    }
    return targetPositionInches; // fallback: accept blocked position
}
/**
 * Computes the ideal clip target positions for a single rail segment.
 * Segments >= 60" receive three clips (edge, midpoint, edge);
 * shorter segments receive two clips (inset from each end).
 */
function computeIdealClipTargetPositions(segmentStartInches, segmentLengthInches) {
    if (segmentLengthInches >= 60) {
        return [
            segmentStartInches + CLIP_EDGE,
            segmentStartInches + (segmentLengthInches / 2),
            segmentStartInches + segmentLengthInches - CLIP_EDGE
        ];
    }
    const edgeInsetInches = segmentLengthInches > CLIP_EDGE * 2.5 ? CLIP_EDGE : segmentLengthInches * 0.1;
    return [
        segmentStartInches + edgeInsetInches,
        segmentStartInches + segmentLengthInches - edgeInsetInches
    ];
}
/**
 * Deduplicates an ordered array of resolved clip positions, discarding any position
 * that falls within the minimum clip spacing of the previously accepted position.
 */
function deduplicateClipPositionsByMinimumSpacing(resolvedClipPositions) {
    const acceptedPositions = [resolvedClipPositions[0]];
    for (let i = 1; i < resolvedClipPositions.length; i++) {
        const lastAcceptedPosition = acceptedPositions[acceptedPositions.length - 1];
        if (resolvedClipPositions[i] > lastAcceptedPosition + CLIP_MIN_SP) {
            acceptedPositions.push(resolvedClipPositions[i]);
        }
    }
    return acceptedPositions;
}
/**
 * Places wall-mounting clips along all segments of a single rail member, routing
 * around obstruction zones at splice joints and internal support intersections.
 *
 * Returns clip positions expressed relative to each segment's local start,
 * plus a warning flag for any segment where the required minimum clip count
 * could not be satisfied.
 */
function calculateClipPlacement(segmentStartPositions, segmentLengths, obstructionCenterPositions) {
    const totalSegmentCount = segmentLengths?.length ?? 0;
    const clipsPerSegment = Array.from({ length: totalSegmentCount }, () => []);
    const segmentWarnings = Array.from({ length: totalSegmentCount }, () => ({ isClipPositionForced: false }));
    if (totalSegmentCount === 0)
        return { clipsPerSegment, segmentWarnings };
    for (let segmentIndex = 0; segmentIndex < totalSegmentCount; segmentIndex++) {
        const segmentStartInches = segmentStartPositions[segmentIndex];
        const segmentLengthInches = segmentLengths[segmentIndex];
        const segmentEndInches = segmentStartInches + segmentLengthInches;
        if (!Number.isFinite(segmentLengthInches) || segmentLengthInches <= 0)
            continue;
        const minimumRequiredClipCount = segmentLengthInches >= 60 ? 3 : 2;
        const idealClipTargetPositions = computeIdealClipTargetPositions(segmentStartInches, segmentLengthInches);
        const resolvedClipPositions = idealClipTargetPositions
            .map(targetPosition => findNearestUnobstructedClipPosition(targetPosition, segmentStartInches + 0.5, segmentEndInches - 0.5, obstructionCenterPositions))
            .sort((a, b) => a - b);
        const uniqueClipPositions = deduplicateClipPositionsByMinimumSpacing(resolvedClipPositions);
        // Store positions relative to the segment's local start coordinate
        clipsPerSegment[segmentIndex] = uniqueClipPositions.map(absolutePosition => absolutePosition - segmentStartInches);
        if (uniqueClipPositions.length < minimumRequiredClipCount) {
            segmentWarnings[segmentIndex].isClipPositionForced = true;
        }
    }
    return { clipsPerSegment, segmentWarnings };
}
// ============================================================
// PHASE 3: INTERIOR GRID — Vertical Support Bar and horizontal rib layout
// ============================================================
/**
 * Constrains a value to the closed interval [lowerBound, upperBound].
 */
function clampToRange(value, lowerBound, upperBound) {
    return Math.max(lowerBound, Math.min(upperBound, value));
}
/**
 * Computes the allowable X-position range for Vertical Support Bar centerlines,
 * accounting for the outer frame rail depth and half the spine member width.
 */
function computeVerticalSpineXBounds(frameWidthInches, specs) {
    return {
        minAllowableX: specs.frameDepth + (specs.supportWidth / 2),
        maxAllowableX: frameWidthInches - specs.frameDepth - (specs.supportWidth / 2)
    };
}
/**
 * Computes the allowable Y-position range for horizontal rib centerlines,
 * accounting for the outer frame rail depth and half the rib member width.
 */
function computeHorizontalRibYBounds(frameHeightInches, specs) {
    return {
        minAllowableY: specs.frameDepth + (specs.supportWidth / 2),
        maxAllowableY: frameHeightInches - specs.frameDepth - (specs.supportWidth / 2)
    };
}
/**
 * Determines the X-positions for all Vertical Support Bar members.
 *
 * When the top/bottom rail requires multiple segments, spines are co-located at
 * splice joints (offset 3" toward center). Otherwise, spines are evenly distributed
 * to bring any unsupported panel width below the maximum allowable span.
 */
function computeVerticalSpineXPositions(topBottomRailSegmentData, innerPanelWidthInches, frameWidthInches, specs) {
    const requiresVerticalSpines = topBottomRailSegmentData.segmentCount > 1 || innerPanelWidthInches > specs.maxSpan;
    if (!requiresVerticalSpines)
        return [];
    const { minAllowableX, maxAllowableX } = computeVerticalSpineXBounds(frameWidthInches, specs);
    // Spines co-located at splice joints for multi-segment top/bottom rails
    if (topBottomRailSegmentData.segmentCount > 1) {
        const topBottomSpliceJointXPositions = topBottomRailSegmentData.segmentStartPositions.slice(1);
        return topBottomSpliceJointXPositions.map(splicePositionInches => {
            let candidateX = splicePositionInches + 3.0;
            if (candidateX > maxAllowableX)
                candidateX = splicePositionInches - 3.0;
            return clampToRange(candidateX, minAllowableX, maxAllowableX);
        });
    }
    // Evenly-distributed spines to reduce unsupported span below the maximum
    const spineCountRequired = Math.ceil(innerPanelWidthInches / specs.maxSpan);
    const evenSpanBetweenSpinesInches = innerPanelWidthInches / spineCountRequired;
    const evenlySpacedSpineXPositions = [];
    for (let spineIndex = 1; spineIndex < spineCountRequired; spineIndex++) {
        evenlySpacedSpineXPositions.push(clampToRange(specs.frameDepth + (evenSpanBetweenSpinesInches * spineIndex), minAllowableX, maxAllowableX));
    }
    return evenlySpacedSpineXPositions;
}
/**
 * Determines the Y-positions for all horizontal rib members.
 *
 * When the side rail requires multiple segments, ribs are co-located at
 * splice joints (offset 3" toward center). Otherwise, ribs are evenly distributed
 * to bring any unsupported panel height below the maximum allowable span.
 */
function computeHorizontalRibYPositions(sideRailSegmentData, innerPanelHeightInches, frameHeightInches, specs) {
    const requiresHorizontalRibs = sideRailSegmentData.segmentCount > 1 || innerPanelHeightInches > specs.maxSpan;
    if (!requiresHorizontalRibs)
        return [];
    const { minAllowableY, maxAllowableY } = computeHorizontalRibYBounds(frameHeightInches, specs);
    // Ribs co-located at splice joints for multi-segment side rails
    if (sideRailSegmentData.segmentCount > 1) {
        const sideSpliceJointYPositions = sideRailSegmentData.segmentStartPositions.slice(1);
        return sideSpliceJointYPositions.map(splicePositionInches => {
            let candidateY = splicePositionInches + 3.0;
            if (candidateY > maxAllowableY)
                candidateY = splicePositionInches - 3.0;
            return clampToRange(candidateY, minAllowableY, maxAllowableY);
        });
    }
    // Evenly-distributed ribs to reduce unsupported span below the maximum
    const ribCountRequired = Math.ceil(innerPanelHeightInches / specs.maxSpan);
    const evenSpanBetweenRibsInches = innerPanelHeightInches / ribCountRequired;
    const evenlySpacedRibYPositions = [];
    for (let ribIndex = 1; ribIndex < ribCountRequired; ribIndex++) {
        evenlySpacedRibYPositions.push(clampToRange(specs.frameDepth + (evenSpanBetweenRibsInches * ribIndex), minAllowableY, maxAllowableY));
    }
    return evenlySpacedRibYPositions;
}
/**
 * Divides the full inner panel height into Vertical Support Bar segments, each no longer
 * than the maximum splice length. Returns the segment count and uniform segment length.
 */
function computeVerticalSpineSegmentation(innerPanelHeightInches, specs) {
    const verticalSpineSegmentCount = Math.max(1, Math.ceil(innerPanelHeightInches / specs.maxSplice));
    const verticalSpineSegmentLengthInches = verticalSpineSegmentCount > 0 ? innerPanelHeightInches / verticalSpineSegmentCount : 0;
    return { verticalSpineSegmentCount, verticalSpineSegmentLengthInches };
}
// ============================================================
// PHASE 4: BILL OF MATERIALS — Material quantities and cut lists
// ============================================================
/**
 * Groups segment lengths by value and doubles the quantity to account for the
 * mirrored (paired) member on the opposite side of the frame
 * (e.g., each top rail segment is matched by an identical bottom rail segment).
 */
function aggregateSegmentLengthsForPairedRailMembers(segmentLengths) {
    const lengthFrequencyTally = {};
    segmentLengths.forEach(lengthInches => {
        const key = lengthInches.toFixed(2);
        lengthFrequencyTally[key] = (lengthFrequencyTally[key] ?? 0) + 1;
    });
    return Object.entries(lengthFrequencyTally).map(([lengthKey, occurrenceCount]) => ({
        lengthInches: parseFloat(lengthKey),
        quantity: occurrenceCount * 2 // doubled for the mirrored opposite rail
    }));
}
/**
 * Generates BOM line items for the four outer frame rails
 * (top, bottom, left, right), grouped by segment length.
 */
function generateOuterFrameRailBOMEntries(topBottomRailSegmentLengths, sideRailSegmentLengths) {
    const outerFrameRailEntries = [];
    aggregateSegmentLengthsForPairedRailMembers(topBottomRailSegmentLengths).forEach(railEntry => {
        outerFrameRailEntries.push({
            name: `Outer Frame Top/Bottom Rail (${railEntry.lengthInches}")`,
            qty: railEntry.quantity,
            len: railEntry.lengthInches,
            type: BOM_TYPES.FRAME
        });
    });
    aggregateSegmentLengthsForPairedRailMembers(sideRailSegmentLengths).forEach(railEntry => {
        outerFrameRailEntries.push({
            name: `Outer Frame Side Rail (${railEntry.lengthInches}")`,
            qty: railEntry.quantity,
            len: railEntry.lengthInches,
            type: BOM_TYPES.FRAME
        });
    });
    return outerFrameRailEntries;
}
/**
 * Generates a BOM line item for all Vertical Support Bar members.
 * Each spine runs the full inner panel height, segmented at splice joints.
 */
function generateVerticalSpineBOMEntries(verticalSpineXPositions, verticalSpineSegmentCount, verticalSpineSegmentLengthInches) {
    if (verticalSpineXPositions.length === 0)
        return [];
    return [{
            name: BOM_NAMES.VERTICAL_SUPPORT_BAR,
            qty: verticalSpineXPositions.length * verticalSpineSegmentCount,
            len: Math.max(0, verticalSpineSegmentLengthInches),
            type: BOM_TYPES.SUPPORT
        }];
}
/**
 * Generates BOM line items for horizontal rib members spanning each bay between
 * adjacent Vertical Support Bars (or the full inner width when no spines are present).
 *
 * Bay lengths are measured as the clear span between support member faces.
 */
function generateHorizontalRibBOMEntries(horizontalRibYPositions, verticalSpineXPositions, frameWidthInches, specs) {
    if (horizontalRibYPositions.length === 0)
        return [];
    const hw = specs.supportWidth / 2;
    // Bay boundaries: each bay runs from a left edge to the corresponding right edge.
    // When there are no spines, this produces a single full-width bay naturally.
    const leftEdges = [specs.frameDepth, ...verticalSpineXPositions.map(x => x + hw)];
    const rightEdges = [...verticalSpineXPositions.map(x => x - hw), frameWidthInches - specs.frameDepth];
    return leftEdges.map((left, i) => ({
        name: `${BOM_NAMES.HORIZONTAL_SUPPORT_BAR} [Segment ${i + 1}]`,
        qty: horizontalRibYPositions.length,
        len: Math.max(0, rightEdges[i] - left),
        type: BOM_TYPES.SUPPORT,
    }));
}
/**
 * Assembles the complete Bill of Materials for the frame, combining outer rails,
 * Vertical Support Bars, and Horizontal Support Bar members into a single cut list.
 */
function generateBOM(frameWidthInches, topBottomRailSegmentLengths, sideRailSegmentLengths, verticalSpineXPositions, horizontalRibYPositions, verticalSpineSegmentCount, verticalSpineSegmentLengthInches, specs) {
    return [
        ...generateOuterFrameRailBOMEntries(topBottomRailSegmentLengths, sideRailSegmentLengths),
        ...generateVerticalSpineBOMEntries(verticalSpineXPositions, verticalSpineSegmentCount, verticalSpineSegmentLengthInches),
        ...generateHorizontalRibBOMEntries(horizontalRibYPositions, verticalSpineXPositions, frameWidthInches, specs)
    ];
}
// ============================================================
// PHASE 5: HARDWARE COUNTS — Connector and clip tallies
// ============================================================
/**
 * Counts the total number of splice connectors required across all frame rails
 * and Vertical Support Bar members. Each splice joint consumes two connectors
 * (one per mating segment end).
 */
function countSpliceConnectors(topBottomRailSegmentCount, sideRailSegmentCount, verticalSpineSegmentCount, verticalSpineCount) {
    const frameRailSpliceJointCount = (topBottomRailSegmentCount - 1) * 2 + // top and bottom outer rails
        (sideRailSegmentCount - 1) * 2; // left and right outer rails
    const verticalSpineSpliceJointCount = (verticalSpineSegmentCount - 1) * verticalSpineCount * 2; // both mating faces per joint
    return frameRailSpliceJointCount + verticalSpineSpliceJointCount;
}
/**
 * Counts the total number of support end clips required.
 * Each Horizontal Support Bar terminus receives one clip (two clips per rib).
 * Each Vertical Support Bar receives one clip at the top and one at the bottom.
 */
function countSupportEndClips(bom, verticalSpineCount) {
    const horizontalRibEndClipCount = bom
        .filter(entry => entry.name.includes(BOM_NAMES.HORIZONTAL_SUPPORT_BAR))
        .reduce((total, entry) => total + entry.qty, 0) * 2;
    const verticalSpineEndClipCount = verticalSpineCount * 2;
    return horizontalRibEndClipCount + verticalSpineEndClipCount;
}
/**
 * Sums the total linear footage of all outer frame rail members from the BOM.
 */
function sumFrameRailTotalLinearFeet(bom) {
    const totalLinearInches = bom
        .filter(entry => entry.type === BOM_TYPES.FRAME)
        .reduce((total, entry) => total + (entry.len * entry.qty), 0);
    return totalLinearInches / 12;
}
/**
 * Sums the total linear footage of all internal support bar members from the BOM.
 */
function sumSupportBarTotalLinearFeet(bom) {
    const totalLinearInches = bom
        .filter(entry => entry.type === BOM_TYPES.SUPPORT)
        .reduce((total, entry) => total + (entry.len * entry.qty), 0);
    return totalLinearInches / 12;
}
/**
 * Assembles the complete hardware count summary for the frame.
 *
 * hangingOption 'clips': wall-mounting clips are used; screws/anchors = 1 per clip
 *   position plus 1 spare (clip positions = wallMountingClipCount / 2).
 * hangingOption 'cleats': cleats are used instead of clips; screws/anchors = 2 per
 *   cleat plus 1 spare.
 */
function computeHardwareCounts(topBottomRailSegmentCount, sideRailSegmentCount, verticalSpineSegmentCount, verticalSpineCount, wallMountingClipCount, cleatCount, bom, hangingOption) {
    const totalSpliceConnectors = countSpliceConnectors(topBottomRailSegmentCount, sideRailSegmentCount, verticalSpineSegmentCount, verticalSpineCount);
    const totalSupportEndClips = countSupportEndClips(bom, verticalSpineCount);
    const hasSupports = verticalSpineCount > 0 || bom.some(e => e.name.includes(HARDWARE_LABELS.SUPPORT_BAR));
    let hangingClips;
    let cleats;
    let screws;
    let anchors;
    if (hangingOption === HangingType.CLEATS) {
        hangingClips = 0;
        cleats = cleatCount;
        screws = cleatCount * 2;
        anchors = cleatCount * 2;
    }
    else {
        // 'clips' — 1 screw/anchor per clip position
        hangingClips = wallMountingClipCount;
        cleats = 0;
        screws = hangingClips;
        anchors = hangingClips;
    }
    return {
        cornerConnectors: 4,
        spliceConnectors: totalSpliceConnectors,
        supportEndClips: totalSupportEndClips,
        spliceBarAllenKey: hasSupports ? 1 : 0,
        hangingClips,
        cleats,
        screws,
        anchors
    };
}
// ============================================================
// PHASE 6: FINANCIAL SUMMARY — Cost and margin calculations
// ============================================================
/**
 * Computes the total raw material and hardware cost at contractor cost rates,
 * including frame rail, support bar, all hardware components, and print media.
 */
function computeTotalRawMaterialCost(frameRailLinearFeet, supportBarLinearFeet, hardwareCounts, squareFootage, rawCosts) {
    const frameRailMaterialCost = frameRailLinearFeet * rawCosts.frameRail;
    const supportBarMaterialCost = supportBarLinearFeet * rawCosts.supportBar;
    const printMediaCost = squareFootage * rawCosts.printPerSqFt;
    const hardwareCost = (4 * rawCosts.cornerConn) +
        (hardwareCounts.spliceConnectors * rawCosts.spliceConn) +
        (hardwareCounts.supportEndClips * rawCosts.supportEndClip) +
        (hardwareCounts.hangingClips * rawCosts.hangingTab) +
        (hardwareCounts.cleats * rawCosts.cleat) +
        (hardwareCounts.screws * rawCosts.screw) +
        (hardwareCounts.anchors * rawCosts.anchor);
    return frameRailMaterialCost + supportBarMaterialCost + hardwareCost + printMediaCost;
}
/**
 * Computes the customer-facing selling price using the active pricing tier rates
 * applied to the frame perimeter footage and fabric print area.
 * Returns the grand total and the per-component sub-totals for display.
 */
function computeCustomerSellingPrice(perimeterFeet, squareFootage, tierPrices, activePricingTier) {
    const activeTierRates = tierPrices[activePricingTier];
    const sellFrame = perimeterFeet * activeTierRates.frame;
    const sellFabric = squareFootage * activeTierRates.fabric;
    return { grandTotal: sellFrame + sellFabric, sellFrame, sellFabric };
}
/**
 * Derives gross margin dollars and gross margin percentage from
 * selling price and total raw cost.
 */
function computeGrossMarginSummary(customerSellingPrice, totalRawMaterialCost) {
    const grossMargin = customerSellingPrice - totalRawMaterialCost;
    const grossMarginPercentage = customerSellingPrice > 0 ? (grossMargin / customerSellingPrice) * 100 : 0;
    return { grossMargin, grossMarginPercentage };
}
/**
 * Orchestrates the full financial analysis: hardware counts, raw material cost,
 * customer selling price, and gross margin.
 */
function calculateFinances(state, billOfMaterials, perimeterFeet, squareFootage, wallMountingClipCount, topBottomRailSegmentCount, sideRailSegmentCount, verticalSpineSegmentCount, verticalSpineCount, cleatCount, hangingOption) {
    const hardwareCounts = computeHardwareCounts(topBottomRailSegmentCount, sideRailSegmentCount, verticalSpineSegmentCount, verticalSpineCount, wallMountingClipCount, cleatCount, billOfMaterials, hangingOption);
    const frameRailLinearFeet = sumFrameRailTotalLinearFeet(billOfMaterials);
    const supportBarLinearFeet = sumSupportBarTotalLinearFeet(billOfMaterials);
    const totalRawMaterialCost = computeTotalRawMaterialCost(frameRailLinearFeet, supportBarLinearFeet, hardwareCounts, squareFootage, state.rawCosts);
    const { grandTotal: customerSellingPrice, sellFrame, sellFabric } = computeCustomerSellingPrice(perimeterFeet, squareFootage, state.tierPrices, state.pricingTier);
    const { grossMargin, grossMarginPercentage } = computeGrossMarginSummary(customerSellingPrice, totalRawMaterialCost);
    return {
        hardwareCounts,
        summary: {
            perimeterFeet,
            squareFootage,
            grandTotal: customerSellingPrice,
            sellFrame,
            sellFabric,
            totalCost: totalRawMaterialCost,
            grossMargin,
            grossMarginPercentage
        }
    };
}
// ============================================================
// ASSEMBLY GUIDE — Step-by-step installation sequence
// ============================================================
/**
 * Generates the ordered list of assembly steps for a computed frame,
 * conditionally including steps for splice connectors and interior supports.
 */
export function buildSteps(data) {
    const steps = [];
    let n = 1;
    steps.push({
        type: ASSEMBLY_STEP_TYPES.ORGANIZE,
        title: `${n++}. Organize Parts`,
        icon: '📦',
        desc: 'Layout all frame bars on the floor. Match the labeled Side bars (Left & Right). The remaining bars are for the Top and Bottom and are interchangeable.',
    });
    steps.push({
        type: ASSEMBLY_STEP_TYPES.CORNERS,
        title: `${n++}. Assemble Corners`,
        icon: '📐',
        desc: 'Insert the corner connector hardware into the frame extrusion channel and tighten the screws with the provided allen key.'
    });
    if (data.hw.spliceConnectors > 0) {
        steps.push({
            type: ASSEMBLY_STEP_TYPES.SPLICES,
            title: `${n++}. Install Splices`,
            icon: '🔗',
            desc: 'Insert the splice connector hardware into the frame extrusion channel and tighten the screws with the provided allen key.',
        });
    }
    if (data.grid.verticalSpineXPositions.length > 0 || data.grid.horizontalRibYPositions.length > 0) {
        steps.push({
            type: ASSEMBLY_STEP_TYPES.SUPPORTS,
            title: `${n++}. Install Supports`,
            icon: '📏',
            desc: data.hw.spliceConnectors > 0
                ? 'Place a support bar next to each splice connector and insert the end of the bar into the frame channel. Tighten screw to secure in place.'
                : 'Install vertical support bars evenly spaced across the frame. Insert the end of the bar into the frame channel and tighten the screw to secure.',
        });
    }
    steps.push({
        type: ASSEMBLY_STEP_TYPES.WALL_BRACKETS,
        title: `${n++}. Wall Brackets`,
        icon: '🧱',
        desc: 'Insert the wall clips into the inside channel of the frame extrusions (Top, Bottom, and Sides). Use two wall clips per frame segment (or 3 for segments > 60").',
    });
    steps.push({
        type: ASSEMBLY_STEP_TYPES.HANG,
        title: `${n++}. Hang Frame`,
        icon: '🖼️',
        desc: 'Hold the frame up to the wall, ensure it is level, and mark drill holes. Drill holes, insert anchors, align wall clips, and attach screws.',
    });
    steps.push({
        type: ASSEMBLY_STEP_TYPES.GRAPHIC,
        title: `${n++}. Install Graphic`,
        icon: '✨',
        desc: 'Start by pressing the silicone gasket into the 4 corners. Then work from the center of each side out to the corners.',
    });
    return steps;
}
// ============================================================
// PRIMARY ORCHESTRATOR — Full frame computation
// ============================================================
/**
 * Computes the complete structural, material, and financial data for an SEG frame
 * given its outer dimensions and pricing configuration.
 *
 * Returns null if the provided dimensions are invalid or zero.
 */
export function computeData(state) {
    const frameWidthInches = parseFloat(state?.dims?.w) || 0;
    const frameHeightInches = parseFloat(state?.dims?.h) || 0;
    if (frameWidthInches <= 0 || frameHeightInches <= 0)
        return null;
    const quantity = parseInt(state?.quantity, 10) || 1;
    const specs = FRAME_SPECS;
    // Derived panel area and perimeter measurements
    const innerPanelWidthInches = Math.max(0, frameWidthInches - (specs.frameDepth * 2));
    const innerPanelHeightInches = Math.max(0, frameHeightInches - (specs.frameDepth * 2));
    const squareFootage = (frameWidthInches * frameHeightInches) / 144;
    const perimeterFeet = ((frameWidthInches * 2) + (frameHeightInches * 2)) / 12;
    // Rail segmentation: split each outer rail at splice joints
    const topBottomRailSegmentData = buildSegments(frameWidthInches, specs.maxSplice);
    const sideRailSegmentData = buildSegments(frameHeightInches, specs.maxSplice);
    // Splice joint center positions (used as clip obstruction zones)
    const topBottomSpliceJointXPositions = topBottomRailSegmentData.segmentStartPositions.slice(1);
    const sideSpliceJointYPositions = sideRailSegmentData.segmentStartPositions.slice(1);
    // Interior structural grid: Vertical Support Bars and Horizontal Support Bars
    const verticalSpineXPositions = computeVerticalSpineXPositions(topBottomRailSegmentData, innerPanelWidthInches, frameWidthInches, specs);
    const horizontalRibYPositions = computeHorizontalRibYPositions(sideRailSegmentData, innerPanelHeightInches, frameHeightInches, specs);
    const { verticalSpineSegmentCount, verticalSpineSegmentLengthInches } = computeVerticalSpineSegmentation(innerPanelHeightInches, specs);
    // Wall-mounting clip placement on all four outer rails
    const topBottomRailClipPlacement = calculateClipPlacement(topBottomRailSegmentData.segmentStartPositions, topBottomRailSegmentData.segmentLengths, [...verticalSpineXPositions, ...topBottomSpliceJointXPositions]);
    const sideRailClipPlacement = calculateClipPlacement(sideRailSegmentData.segmentStartPositions, sideRailSegmentData.segmentLengths, [...horizontalRibYPositions, ...sideSpliceJointYPositions]);
    // Two physical tabs per mounting position (paired hardware assembly)
    const tabsPerMountingPosition = 2;
    const totalWallMountingClips = [...topBottomRailClipPlacement.clipsPerSegment, ...sideRailClipPlacement.clipsPerSegment]
        .reduce((total, segmentClips) => total + segmentClips.length * tabsPerMountingPosition, 0);
    // Bill of Materials and financial summary
    const billOfMaterials = generateBOM(frameWidthInches, topBottomRailSegmentData.segmentLengths, sideRailSegmentData.segmentLengths, verticalSpineXPositions, horizontalRibYPositions, verticalSpineSegmentCount, verticalSpineSegmentLengthInches, specs);
    // Cleats mount on the top rail only — 1 per top rail segment
    const hangingOption = state?.hangingOption ?? HangingType.CLIPS;
    const cleatCount = hangingOption === HangingType.CLEATS
        ? topBottomRailSegmentData.segmentCount
        : 0;
    const finances = calculateFinances(state, billOfMaterials, perimeterFeet, squareFootage, totalWallMountingClips, topBottomRailSegmentData.segmentCount, sideRailSegmentData.segmentCount, verticalSpineSegmentCount, verticalSpineXPositions.length, cleatCount, hangingOption);
    if (quantity) {
        // Multiply BOM quantities
        billOfMaterials.forEach(item => {
            item.qty *= quantity;
        });
        // Multiply hardware counts
        for (const key in finances.hardwareCounts) {
            finances.hardwareCounts[key] *= quantity;
        }
        // add in 1 extra screw/anchor for each frame qty
        if (finances.hardwareCounts.anchors) {
            finances.hardwareCounts.anchors += quantity;
        }
        if (finances.hardwareCounts.screws) {
            finances.hardwareCounts.screws += quantity;
        }
        // Multiply financial summary figures
        finances.summary.perimeterFeet *= quantity;
        finances.summary.squareFootage *= quantity;
        finances.summary.grandTotal *= quantity;
        finances.summary.sellFrame *= quantity;
        finances.summary.sellFabric *= quantity;
        finances.summary.totalCost *= quantity;
        finances.summary.grossMargin *= quantity;
        // Gross margin percentage remains correct as it's a ratio of totals
    }
    return {
        dims: { w: frameWidthInches, h: frameHeightInches },
        specs,
        bom: billOfMaterials,
        hw: finances.hardwareCounts,
        finance: finances.summary,
        grid: {
            verticalSpineXPositions,
            horizontalRibYPositions,
            verticalSpineSegmentCount,
            verticalSpineSegmentLength: verticalSpineSegmentLengthInches
        },
        frame: {
            topBottomSegments: topBottomRailSegmentData.segmentCount,
            sideSegments: sideRailSegmentData.segmentCount,
            clipsTopBottom: topBottomRailClipPlacement.clipsPerSegment,
            clipsLeftRight: sideRailClipPlacement.clipsPerSegment,
            topBottomLengths: topBottomRailSegmentData.segmentLengths,
            sideLengths: sideRailSegmentData.segmentLengths,
            topBottomSegmentStartPositions: topBottomRailSegmentData.segmentStartPositions,
            sideSegmentStartPositions: sideRailSegmentData.segmentStartPositions,
        },
        clipWarning: topBottomRailClipPlacement.segmentWarnings.some(w => w.isClipPositionForced) ||
            sideRailClipPlacement.segmentWarnings.some(w => w.isClipPositionForced)
    };
}
